import { PZAttachment, PZDocument, PZItem, PZStatus, PZStockValuation } from '../types/pz';
import {
  asArray,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeTransportRouteSummary, TransportRouteSummaryDto } from './transport';
import { normalizeBlockedActions } from '../modules/warehouse/blockedActions';
import {
  getPersistedQuantityForUnit,
  READABLE_QUANTITY_UNITS,
  type QuantityUnit,
} from '../utils/commercialQuantity';

const PZ_STATUSES: PZStatus[] = [
  PZStatus.DRAFT,
  PZStatus.CONFIRMED,
  PZStatus.RECEIVED,
  PZStatus.CANCELLED,
];

export interface PZDocumentDto extends PZDocument {
  items: PZItem[];
  attachments: PZAttachment[];
  sourceTransports: TransportRouteSummaryDto[];
  itemsLoaded: boolean;
  attachmentsLoaded: boolean;
  sourceTransportsLoaded: boolean;
}

export function normalizePZDocument(raw: unknown, entity = 'pzDocument'): PZDocumentDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const supplier = normalizePZSupplier(record?.supplier, `${entity}.supplier`);
  const createdBy = normalizePZCreatedBy(record?.createdBy, `${entity}.createdBy`);
  const items = asOptionalArray(record?.items, (item, index) => normalizePZItem(item, `${entity}.items[${index}]`));
  const attachments = asOptionalArray(
    record?.attachments,
    (item, index) => normalizePZAttachment(item, `${entity}.attachments[${index}]`),
  );
  const sourceTransports = asOptionalArray(
    record?.sourceTransports,
    (item, index) => normalizeTransportRouteSummary(item, `${entity}.sourceTransports[${index}]`),
  );

  return {
    id: asString(record?.id),
    pzNumber: asString(record?.pzNumber),
    invoicePending: record?.invoicePending === true,
    purchaseInvoiceId: asOptionalString(record?.purchaseInvoiceId) ?? null,
    withdrawnPurchaseInvoice: record?.withdrawnPurchaseInvoice && typeof record.withdrawnPurchaseInvoice === 'object'
      ? { number: asOptionalString((record.withdrawnPurchaseInvoice as Record<string, unknown>).number) ?? null }
      : null,
    supplierId: asOptionalString(record?.supplierId),
    supplier,
    issueDate: asString(record?.issueDate),
    receiptDate: asString(record?.receiptDate),
    expectedDeliveryDate: asOptionalString(record?.expectedDeliveryDate),
    supplierName: asOptionalString(record?.supplierName),
    supplierAddress: asOptionalString(record?.supplierAddress),
    supplierNIP: asOptionalString(record?.supplierNIP),
    supplierInvoiceNumber: asOptionalString(record?.supplierInvoiceNumber),
    supplierInvoiceDate: asOptionalString(record?.supplierInvoiceDate),
    supplierInvoiceAmount: optionalNumber(record?.supplierInvoiceAmount),
    supplierInvoiceCurrency: asOptionalString(record?.supplierInvoiceCurrency),
    recipientName: asOptionalString(record?.recipientName),
    recipientAddress: asOptionalString(record?.recipientAddress),
    recipientNIP: asOptionalString(record?.recipientNIP),
    warehouseLocation: asOptionalString(record?.warehouseLocation),
    // Etap 2: null (not undefined) for a manual PZ and for any response from a
    // backend that does not expose it yet — callers then fall back to whatever
    // scope they were navigated with, never to a fabricated id.
    sourceSupplierOrderId: asOptionalString(record?.sourceSupplierOrderId) ?? null,
    sourceTransportId: record?.sourceTransportId === null ? null : asOptionalString(record?.sourceTransportId),
    sourceTransportIds: asArray(record?.sourceTransportIds, (item) => asString(item)).filter(Boolean),
    sourceTransports: sourceTransports ?? [],
    transportCompany: asOptionalString(record?.transportCompany),
    transportInvoiceNo: asOptionalString(record?.transportInvoiceNo),
    transportCost: optionalNumber(record?.transportCost),
    transportCurrency: asOptionalString(record?.transportCurrency),
    vehicleRegistration: asOptionalString(record?.vehicleRegistration),
    driverName: asOptionalString(record?.driverName),
    driverPhone: asOptionalString(record?.driverPhone),
    totalQuantity: optionalNumber(record?.totalQuantity),
    totalNetWeight: optionalNumber(record?.totalNetWeight),
    totalGrossWeight: optionalNumber(record?.totalGrossWeight),
    totalValue: optionalNumber(record?.totalValue),
    acceptedReceiptCostPLN: optionalNumber(record?.acceptedReceiptCostPLN),
    currentBatchValuePLN: optionalNumber(record?.currentBatchValuePLN),
    currency: asString(record?.currency, 'PLN'),
    allocateTransportCost: typeof record?.allocateTransportCost === 'boolean' ? record.allocateTransportCost : undefined,
    allocateCustomsCost: typeof record?.allocateCustomsCost === 'boolean' ? record.allocateCustomsCost : undefined,
    status: normalizePZStatus(record?.status, `${entity}.status`),
    approvedBy: asOptionalString(record?.approvedBy),
    receivedBy: asOptionalString(record?.receivedBy),
    confirmedAt: asOptionalString(record?.confirmedAt),
    receivedAt: asOptionalString(record?.receivedAt),
    notes: asOptionalString(record?.notes),
    internalNotes: asOptionalString(record?.internalNotes),
    createdById: asOptionalString(record?.createdById),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
    createdBy,
    // Backend-computed "why is this unavailable" (never re-derived in pages).
    blockedActions: normalizeBlockedActions(record?.blockedActions),
    // WP1: the PZ's own freight/customs/other copy is not in the batch value.
    ownStockCostsIgnored: record?.ownStockCostsIgnored === true,
    // PZ card valuation alert (stockValuationService.receiptValuation): absent
    // when redacted for a viewer without purchase-price visibility, null when
    // the PZ has no source ZD, an object otherwise. Preserve all three.
    stockValuation: normalizePZStockValuation(record?.stockValuation, `${entity}.stockValuation`),
    stornoAt: asOptionalString(record?.stornoAt) ?? null,
    stornoReason: asOptionalString(record?.stornoReason) ?? null,
    stornoByName: asOptionalString(record?.stornoByName) ?? null,
    items: items ?? [],
    attachments: attachments ?? [],
    itemsLoaded: items !== undefined,
    attachmentsLoaded: attachments !== undefined,
    sourceTransportsLoaded: sourceTransports !== undefined,
  };
}

export function normalizePZDocuments(raw: unknown, entity = 'pzDocuments'): PZDocumentDto[] {
  return asArray(raw, (item, index) => normalizePZDocument(item, `${entity}[${index}]`));
}

function normalizePZStockValuation(raw: unknown, entity: string): PZStockValuation | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return null;

  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
    return null;
  }

  return {
    extraTotalPLN: asNumber(record.extraTotalPLN),
    freightPLN: optionalNumber(record.freightPLN) ?? null,
    ...(record.freightSource === 'TRUCK' || record.freightSource === 'AVERAGED' ? { freightSource: record.freightSource } : {}),
    ...(Array.isArray(record.freightTruckLabels) ? { freightTruckLabels: asArray(record.freightTruckLabels, (item) => asString(item)) } : {}),
    landedPLN: optionalNumber(record.landedPLN) ?? null,
    allocationUnit: asOptionalString(record.allocationUnit) ?? null,
    valuationComplete: record.valuationComplete === true,
    issues: asArray(record.issues, (item) => asString(item)),
  };
}

function normalizePZStatus(raw: unknown, entity: string): PZStatus {
  if (typeof raw === 'string' && PZ_STATUSES.includes(raw as PZStatus)) {
    return raw as PZStatus;
  }

  reportDtoAnomaly(entity, 'status', raw);
  return PZStatus.DRAFT;
}

function normalizePZSupplier(raw: unknown, entity: string): PZDocument['supplier'] {
  const record = asRecord(raw);
  if (!record) return undefined;

  return {
    id: asString(record.id),
    name: asString(record.name),
    email: asOptionalString(record.email),
    phone: asOptionalString(record.phone),
    address: asOptionalString(record.address),
    taxId: asOptionalString(record.taxId),
  };
}

function normalizePZCreatedBy(raw: unknown, entity: string): PZDocument['createdBy'] {
  const record = asRecord(raw);
  if (!record) return undefined;

  return {
    email: asString(record.email),
    name: asOptionalString(record.name),
  };
}

function normalizePZItem(raw: unknown, entity: string): PZItem {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }
  const conversionUnit = normalizePZConversionUnit(record?.conversionUnit, `${entity}.conversionUnit`);
  const persistedConversionQuantity = getPersistedQuantityForUnit(record, conversionUnit);

  return {
    id: asString(record?.id),
    pzDocumentId: asString(record?.pzDocumentId),
    purchaseInvoiceItemId: asOptionalString(record?.purchaseInvoiceItemId) ?? null,
    sourceSupplierOrderItemId: asOptionalString(record?.sourceSupplierOrderItemId),
    // Defaults to false: an older response carries no lock flag, and guessing
    // "locked" from the stripped trace id above would lock nothing (it is never
    // present) while guessing "unlocked" is exactly today's behaviour.
    supplierOrderTraceLocked: record?.supplierOrderTraceLocked === true,
    productVariantId: asOptionalString(record?.productVariantId),
    productName: asOptionalString(record?.productName),
    productCode: asOptionalString(record?.productCode),
    sku: asOptionalString(record?.sku),
    ean: asOptionalString(record?.ean),
    unit: asOptionalString(record?.unit),
    quantityOrdered: asNumber(record?.quantityOrdered),
    quantityPcs: optionalNullableNumber(record?.quantityPcs),
    quantityM2: optionalNullableNumber(record?.quantityM2),
    quantityM3: optionalNullableNumber(record?.quantityM3),
    // NULL (not yet entered) must stay null, not collapse to 0 — the PZ form
    // has no input for it, so an edit save would otherwise silently re-send
    // an explicit 0 and stock zero units (migration 097 / backend/src/services/pzService.js).
    quantityReceived: optionalNullableNumber(record?.quantityReceived) ?? null,
    quantityReceivedPcs: optionalNullableNumber(record?.quantityReceivedPcs),
    quantityReceivedM2: optionalNullableNumber(record?.quantityReceivedM2),
    quantityReceivedM3: optionalNullableNumber(record?.quantityReceivedM3),
    quantityDamaged: optionalNumber(record?.quantityDamaged),
    quantityDamagedPcs: optionalNullableNumber(record?.quantityDamagedPcs),
    quantityDamagedM2: optionalNullableNumber(record?.quantityDamagedM2),
    quantityDamagedM3: optionalNullableNumber(record?.quantityDamagedM3),
    width: optionalNullableNumber(record?.width),
    height: optionalNullableNumber(record?.height),
    length: optionalNullableNumber(record?.length),
    thickness: optionalNullableNumber(record?.thickness),
    netWeight: optionalNullableNumber(record?.netWeight),
    grossWeight: optionalNullableNumber(record?.grossWeight),
    productCategoryName: asOptionalString(record?.productCategoryName),
    productCategoryColor: asOptionalString(record?.productCategoryColor),
    productCategoryDimensionDisplayProfile: asOptionalString(record?.productCategoryDimensionDisplayProfile),
    conversionUnit,
    conversionQuantity: record?.conversionQuantity == null
      ? (persistedConversionQuantity == null ? undefined : asNumber(persistedConversionQuantity))
      : asNumber(record.conversionQuantity),
    purchaseQuantity: optionalNullableNumber(record?.purchaseQuantity) ?? null,
    purchaseUnit: normalizePZPricingUnit(record?.purchaseUnit, `${entity}.purchaseUnit`),
    unitPrice: optionalNumber(record?.unitPrice),
    totalPrice: optionalNumber(record?.totalPrice),
    acceptedReceiptCostPLN: optionalNumber(record?.acceptedReceiptCostPLN),
    currentBatchValuePLN: optionalNumber(record?.currentBatchValuePLN),
    exchangeRatePLN: optionalNumber(record?.exchangeRatePLN),
    unitPricePLN: optionalNumber(record?.unitPricePLN),
    totalPricePLN: optionalNumber(record?.totalPricePLN),
    currency: asString(record?.currency, 'PLN'),
    allocatedTransportCost: optionalNumber(record?.allocatedTransportCost),
    allocatedCustomsCost: optionalNumber(record?.allocatedCustomsCost),
    allocatedOtherCost: optionalNumber(record?.allocatedOtherCost),
    totalUnitCost: optionalNumber(record?.totalUnitCost),
    cnCode: asOptionalString(record?.cnCode),
    countryOfOrigin: asOptionalString(record?.countryOfOrigin),
    description: asOptionalString(record?.description),
    notes: asOptionalString(record?.notes),
  };
}

function normalizePZAttachment(raw: unknown, entity: string): PZAttachment {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    id: asString(record?.id),
    pzId: asString(record?.pzId),
    fileName: asString(record?.fileName),
    filePath: asString(record?.filePath),
    fileType: asString(record?.fileType),
    fileSize: asNumber(record?.fileSize),
    description: asOptionalString(record?.description),
    category: asOptionalString(record?.category),
    uploadedAt: asString(record?.uploadedAt),
    uploadedBy: asOptionalString(record?.uploadedBy),
  };
}

function normalizePZConversionUnit(raw: unknown, entity: string): 'pcs' | 'm2' | 'm3' | null {
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }

  if (raw === 'pcs' || raw === 'm2' || raw === 'm3') {
    return raw;
  }

  reportDtoAnomaly(entity, 'conversionUnit', raw);
  return null;
}

function normalizePZPricingUnit(raw: unknown, entity: string): QuantityUnit | null {
  if (raw === undefined || raw === null || raw === '') return null;
  if ((READABLE_QUANTITY_UNITS as readonly unknown[]).includes(raw)) {
    return raw as QuantityUnit;
  }
  reportDtoAnomaly(entity, 'pricingUnit', raw);
  return null;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  return asNumber(value);
}

function optionalNullableNumber(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  return asNumber(value);
}
